import React, { useCallback, useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, MessageSquareText, ArrowLeft, ArrowRight, TrendingUp, Zap, Brain, ArrowDownRight, Clock, BarChart3, Trophy, FileText, CheckCircle2, Users, Target, Network, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
type SlideType = {
  id: number;
  title: string;
  subtitle?: string;
  tagline?: string;
  bullets?: string[];
  columns?: {
    title: string;
    items: string[];
    tint: 'rose' | 'emerald';
  }[];
  table?: {
    scenario: string;
    rev: string;
    wholesale: string;
    adoption: string;
    savings: string;
    share: string;
  }[];
  timeline?: {
    phase: string;
    date: string;
    desc: string;
  }[];
  note: string;
  layout: string;
};
const SLIDES: SlideType[] = [{
  id: 1,
  title: 'BlockQuote',
  subtitle: 'Instant Group Quotes + Holds + Contracts',
  tagline: 'Direct pricing. Faster groups. Less leakage.',
  note: 'BlockQuote is the infrastructure layer for group travel — replacing email-based quoting with instant, direct resort pricing.',
  layout: 'title'
}, {
  id: 2,
  title: 'The Problem',
  bullets: ['Group quoting is slow — email chains, manual quotes, 48-72hr response times', 'Inconsistent terms, pricing errors, low close rates', 'Wholesalers capture 15-25% margin and control distribution', 'No audit trail, no pricing governance, no visibility'],
  note: 'Group travel is the last major hospitality segment still run on email. This creates massive inefficiency and margin leakage.',
  layout: 'problem'
}, {
  id: 3,
  title: 'Why It Matters Now',
  bullets: ['Resorts need margin recovery and distribution control post-COVID', 'Agencies demand speed and transparency — instant wins', 'AI makes messy RFP/quote operations automatable for the first time', 'Wholesale model is under pressure from direct distribution trends'],
  note: 'Three forces converging: resort margin pressure, agency expectations for speed, and AI capabilities that make automation practical.',
  layout: 'why-now'
}, {
  id: 4,
  title: 'Proof: Moon Palace Case Study',
  bullets: ['Built internal instant quoting portal for agencies', 'Reduced/eliminated wholesaler dependency for group segment'],
  note: "This isn't theoretical. Moon Palace proved the model internally. BlockQuote packages this proven playbook for every resort group.",
  layout: 'case-study'
}, {
  id: 5,
  title: 'DestaWed: From Agency to Industry Leader',
  subtitle: 'BlockQuote Investor & Strategic Partner',
  tagline: '"BlockQuote fundamentally changed how we sell. We don\'t wait days for pricing anymore — we close the group while they\'re still excited on the first call."',
  bullets: ['#1 Agency on record for Moon Palace resort group', '3x growth in group bookings volume', 'Strategic investor in BlockQuote', '<2 min average quote-to-proposal time'],
  note: 'DestaWed is proof that the agency side works too. They used BlockQuote to become the largest agency for Moon Palace — and then invested in us. This is the strongest possible signal of product-market fit.',
  layout: 'destawed'
}, {
  id: 6,
  title: 'Before vs After Workflow',
  columns: [{
    title: 'BEFORE',
    tint: 'rose',
    items: ['Email RFP', 'Manual pricing lookup', '48-72hr response', 'Back-and-forth negotiation', 'Lost groups', 'No audit trail']
  }, {
    title: 'AFTER',
    tint: 'emerald',
    items: ['Instant multi-property quote', 'Automated hold with expiry', 'Standardized proposal', 'One-click contract', 'Full audit trail']
  }],
  note: 'The workflow transformation is dramatic. What took days now takes minutes, with full governance and tracking.',
  layout: 'workflow'
}, {
  id: 7,
  title: 'The Product (MVP)',
  bullets: ['Instant multi-property group quotes (rooms, dates, events, packages)', 'Smart holds with automated expiry timers', 'Good/Better/Best package generation within policy', 'Branded proposal generation + one-click export', 'Basic approval workflow + policy rules engine', 'Full audit trail + reporting'],
  note: 'MVP is focused on the core quoting workflow. Everything needed to replace email-based group quoting with instant infrastructure.',
  layout: 'product'
}, {
  id: 8,
  title: 'What Resorts Get',
  bullets: ['Reduce/eliminate wholesaler leakage (estimated $6M+ annual savings per resort group)', '3x faster group conversion (instant quotes vs days)', 'Pricing control + governance (rate floors, concession limits)', '60%+ reduction in manual quoting workload', 'Audit trail for every quote, change, and approval'],
  note: 'Resorts get margin recovery, speed, control, and efficiency. The ROI is immediate and measurable.',
  layout: 'resorts'
}, {
  id: 9,
  title: 'What Agencies Get',
  bullets: ['Instant quote options (Good/Better/Best in seconds)', 'Faster client approvals with real-time availability', 'Track holds, deadlines, and deposits in one dashboard', 'Less back-and-forth, higher win rate', 'Professional proposals generated automatically'],
  note: 'Agencies are our distribution channel. They adopt because BlockQuote makes them faster and more competitive. DestaWed proved this — they became #1 for Moon Palace using our tools.',
  layout: 'agencies'
}, {
  id: 10,
  title: 'Business Model: Win When You Win',
  bullets: ['Platform deployed free or near-free to resort groups', 'BlockQuote takes 5% of verified net savings (Savings = Revenue × Leakage % × Adoption %)', 'Clear baseline: trailing 12-month group segment benchmarked', 'Controlled for seasonality and growth', 'Optional: SaaS fees + transaction fees + premium agency tools'],
  note: 'The savings-share model eliminates adoption friction. We only earn when the resort demonstrably saves money.',
  layout: 'business-model'
}, {
  id: 11,
  title: 'Financial Impact Model',
  table: [{
    scenario: 'Conservative',
    rev: '$50M',
    wholesale: '15%',
    adoption: '40%',
    savings: '$3.0M',
    share: '$150K'
  }, {
    scenario: 'Base',
    rev: '$50M',
    wholesale: '20%',
    adoption: '60%',
    savings: '$6.0M',
    share: '$300K'
  }, {
    scenario: 'Aggressive',
    rev: '$50M',
    wholesale: '25%',
    adoption: '80%',
    savings: '$10.0M',
    share: '$500K'
  }],
  note: 'Savings = Group Room Revenue × Wholesale Leakage % × Adoption %. BQ Share = Savings × 5%. Example per resort group — scales with portfolio size.',
  layout: 'financials'
}, {
  id: 12,
  title: 'RateBrain: AI-Powered Pricing Intelligence',
  bullets: ['Dynamic Rate Optimization: Analyze real-time demand, seasonality, and competitive set pricing to recommend optimal group rates', 'RFP Intelligence: Parse incoming RFPs from any format — email, PDF, WhatsApp — and extract requirements in seconds', 'Competitive Set Analysis: Monitor competitor pricing across your market with real-time rate shopping intelligence', 'Negotiation Copilot: AI-powered counter-offer suggestions that protect margins while maximizing close probability', 'Predictive Analytics: Close-probability scoring, demand forecasting, and price elasticity modeling'],
  note: 'RateBrain is our AI pricing engine. It analyzes competitive pricing, demand patterns, and market intelligence to deliver optimal dynamic pricing for every group quote. Not a black box — transparent recommendations resorts can trust.',
  layout: 'ai'
}, {
  id: 13,
  title: 'Go-to-Market',
  bullets: ['Start with existing resort and agency relationships (Moon Palace, DestaWed)', 'Target: CTO + Head of Revenue + Head of Groups Ops', '90-day pilot with defined KPIs and savings baseline', 'Land-and-expand within resort group portfolios', 'Agency network effects: each resort added increases agency value'],
  note: 'We start where we have relationships — Moon Palace proved the model, DestaWed proved agency adoption. Now we scale to new resort groups.',
  layout: 'gtm'
}, {
  id: 14,
  title: 'Product Roadmap',
  timeline: [{
    phase: 'MVP (Now)',
    date: 'Current',
    desc: 'Instant quotes, holds, proposals, RateBrain pricing intelligence, agency portal'
  }, {
    phase: 'V1',
    date: 'Q2 2026',
    desc: 'Contract templates + e-sign, deposit/payment hooks, PMS/CRS integrations, role-based access'
  }, {
    phase: 'V2',
    date: 'Q4 2026',
    desc: 'Cross-resort benchmarking, close probability scoring, price elasticity, demand forecasting'
  }, {
    phase: 'V3',
    date: '2027',
    desc: 'RateBrain Platform — aggregation + intelligence marketplace, multi-resort rate optimization'
  }],
  note: 'Each phase builds on the data captured in the previous phase. RateBrain is already live for pricing intelligence — V3 expands it into a full platform.',
  layout: 'roadmap'
}, {
  id: 15,
  title: 'Next Steps',
  bullets: ['Live demos available: Resort Dashboard + Agency Portal (try them now)', 'DestaWed confirmed as strategic investor and launch partner', 'Moon Palace case study validated — proven playbook ready to scale', 'Identify 3 pilot resort groups from existing relationships', 'Deploy first pilot within 90 days — defined KPIs + savings baseline', 'Timeline: Demo ready → Pilot live → Results measured → Scale decision'],
  note: "We have a working product, a proven case study, and an investor who became our biggest agency success story. We're looking for pilot partners who want to prove the model. The ask is simple: give us 90 days and a baseline to measure against.",
  layout: 'next-steps'
}];
// Staggered animation variants
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
export function PitchDeck() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [direction, setDirection] = useState(0);
  const slide = SLIDES[currentIndex];
  const paginate = useCallback((newDirection: number) => {
    const newIndex = currentIndex + newDirection;
    if (newIndex >= 0 && newIndex < SLIDES.length) {
      setDirection(newDirection);
      setCurrentIndex(newIndex);
    }
  }, [currentIndex]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') paginate(1);
      if (e.key === 'ArrowLeft') paginate(-1);
      if (e.key === 'n') setShowNotes(prev => !prev);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginate]);
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  // Render specific slide layouts
  const renderSlideContent = (slide: SlideType) => {
    switch (slide.layout) {
      case 'title':
        return <>
            <div className="text-center space-y-8 flex flex-col items-center justify-center min-h-[60vh]" data-id="element-1885">
              <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.2
            }} className="font-mono text-sm uppercase tracking-widest text-red-600" data-id="element-1886">
                {slide.subtitle}
              </motion.p>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="font-serif text-6xl md:text-8xl tracking-tight text-stone-50 leading-[1.1]" data-id="element-1887">
                {slide.title}
              </motion.h1>
              <motion.div initial={{
              scaleX: 0
            }} animate={{
              scaleX: 1
            }} transition={{
              delay: 0.5,
              duration: 0.8
            }} className="w-24 h-1 bg-red-600 mx-auto" data-id="element-1888" />
              <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} className="text-xl md:text-2xl text-stone-400 max-w-2xl mx-auto font-sans" data-id="element-1889">
                {slide.tagline}
              </motion.p>
            </div>
          </>;
      case 'problem':
        return <>
            <div className="space-y-16 w-full" data-id="element-1890">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1891">
                {slide.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 border-b border-stone-800 pb-16" data-id="element-1892">
                {[{
                label: 'Quote Response Time',
                val: '48-72',
                unit: 'hrs',
                desc: 'Groups book elsewhere while you price'
              }, {
                label: 'Wholesale Margin Capture',
                val: '15-25',
                unit: '%',
                desc: 'Revenue lost to indirect distribution'
              }, {
                label: 'Pricing Visibility',
                val: '0',
                unit: '%',
                desc: 'No audit trail, governance, or control'
              }].map((stat, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="space-y-4" data-id="element-1893">
                    <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-1894">
                      {stat.label}
                    </p>
                    <p className="font-mono text-5xl md:text-6xl text-stone-50" data-id="element-1895">
                      {stat.val}
                      <span className="text-red-600 text-3xl ml-1" data-id="element-1896">
                        {stat.unit}
                      </span>
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-1897">{stat.desc}</p>
                  </motion.div>)}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1898">
                {slide.bullets?.map((bullet, i) => <motion.div key={i} custom={i + 3} variants={itemVariants} initial="hidden" animate="visible" className="flex items-start gap-4" data-id="element-1899">
                    <span className="w-1.5 h-1.5 bg-red-600 mt-2.5 flex-shrink-0" data-id="element-1900" />
                    <p className="text-lg text-stone-300" data-id="element-1901">{bullet}</p>
                  </motion.div>)}
              </div>
            </div>
          </>;
      case 'why-now':
        const icons = [TrendingUp, Zap, Brain, ArrowDownRight];
        return <>
            <div className="space-y-12 w-full" data-id="element-1902">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1903">
                {slide.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1904">
                {slide.bullets?.map((bullet, i) => {
                const Icon = icons[i];
                const [title, ...desc] = bullet.split(' — ');
                return <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-8 border border-stone-800 bg-stone-900/30 flex flex-col gap-4" data-id="element-1905">
                      <Icon className="w-8 h-8 text-red-600" data-id="element-1906" />
                      <h3 className="font-serif text-2xl text-stone-50" data-id="element-1907">
                        {title || bullet}
                      </h3>
                      {desc.length > 0 && <p className="text-stone-400 text-lg" data-id="element-1908">
                          {desc.join(' — ')}
                        </p>}
                    </motion.div>;
              })}
              </div>
            </div>
          </>;
      case 'case-study':
        return <>
            <div className="space-y-10 w-full" data-id="element-1909">
              <div data-id="element-1910">
                <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-1911">
                  Proof Point
                </p>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50" data-id="element-1912">
                  {slide.title.replace('Proof: ', '')}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 border border-stone-800" data-id="element-1913">
                {[{
                label: 'Wholesale Leakage Reduced',
                val: '15–25',
                unit: '%',
                desc: 'Estimated leakage shifted to direct (mgmt estimate)'
              }, {
                label: 'Estimated Annual Savings',
                val: '$6.0M',
                unit: '+',
                desc: 'Per resort group at base case adoption (estimate)'
              }, {
                label: 'Quoting Velocity',
                val: '<1',
                unit: 'min',
                desc: 'Down from 48–72 hours'
              }, {
                label: 'Conversion Lift',
                val: '↑',
                unit: 'Higher',
                desc: 'Speed wins group business'
              }].map((stat, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className={`p-8 ${i !== 3 ? 'border-b md:border-b-0 md:border-r border-stone-800' : ''}`} data-id="element-1914">
                    <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-1915">
                      {stat.label}
                    </p>
                    <p className="font-mono text-4xl lg:text-5xl text-stone-50 mb-3" data-id="element-1916">
                      {stat.val}
                      <span className="text-red-600 text-2xl ml-1" data-id="element-1917">
                        {stat.unit}
                      </span>
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-1918">{stat.desc}</p>
                  </motion.div>)}
              </div>
              <motion.div custom={4} variants={itemVariants} initial="hidden" animate="visible" className="mt-8 space-y-3" data-id="element-1919">
                {slide.bullets?.map((bullet, i) => <div key={i} className="flex items-center gap-3 text-stone-300" data-id="element-1920">
                    <CheckCircle2 className="w-5 h-5 text-red-600" data-id="element-1921" />
                    <span className="text-lg" data-id="element-1922">{bullet}</span>
                  </div>)}
              </motion.div>
            </div>
          </>;
      case 'destawed':
        return <>
            <div className="space-y-12 w-full" data-id="element-1923">
              <div className="flex items-center gap-4 mb-4" data-id="element-1924">
                <div className="px-3 py-1 bg-red-600 text-white font-mono text-xs uppercase tracking-widest" data-id="element-1925">
                  BlockQuote Investor
                </div>
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-1926">
                  {slide.subtitle}
                </p>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 mb-8" data-id="element-1927">
                {slide.title}
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-1928">
                {/* Left: Story */}
                <div className="space-y-8" data-id="element-1929">
                  <p className="text-xl text-stone-300 leading-relaxed" data-id="element-1930">
                    DestaWed used BlockQuote's instant quoting infrastructure to
                    become the largest agency on record for Moon Palace resort
                    group. Their ability to quote instantly, hold inventory, and
                    present professional proposals gave them a massive
                    competitive advantage over traditional agencies.
                  </p>
                  <motion.blockquote custom={1} variants={itemVariants} initial="hidden" animate="visible" className="border-l-2 border-red-600 pl-6" data-id="element-1931">
                    <p className="text-lg italic text-stone-400 leading-relaxed" data-id="element-1932">
                      {slide.tagline}
                    </p>
                  </motion.blockquote>
                </div>

                {/* Right: Metrics */}
                <div className="grid grid-cols-2 border border-stone-800" data-id="element-1933">
                  {[{
                  label: 'Status',
                  val: '#1',
                  desc: 'Agency on record for Moon Palace'
                }, {
                  label: 'Volume',
                  val: '3x',
                  desc: 'Group bookings growth'
                }, {
                  label: 'Partnership',
                  val: 'Investor',
                  desc: 'Strategic investor in BlockQuote'
                }, {
                  label: 'Speed',
                  val: '<2 min',
                  desc: 'Quote-to-proposal time'
                }].map((stat, i) => <motion.div key={i} custom={i + 2} variants={itemVariants} initial="hidden" animate="visible" className={`p-8 ${i < 2 ? 'border-b border-stone-800' : ''} ${i % 2 === 0 ? 'border-r border-stone-800' : ''}`} data-id="element-1934">
                      <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-1935">
                        {stat.label}
                      </p>
                      <p className="font-serif text-3xl text-stone-50 mb-2" data-id="element-1936">
                        {stat.val}
                      </p>
                      <p className="text-sm text-stone-400" data-id="element-1937">{stat.desc}</p>
                    </motion.div>)}
                </div>
              </div>

              <motion.div custom={6} variants={itemVariants} initial="hidden" animate="visible" className="mt-8 p-6 border border-stone-800 bg-stone-900/50 flex items-center gap-4" data-id="element-1938">
                <Award className="w-6 h-6 text-red-600 flex-shrink-0" data-id="element-1939" />
                <p className="text-lg text-stone-300" data-id="element-1940">
                  DestaWed's success validates both sides of the marketplace —
                  proving that agencies who adopt BlockQuote gain a decisive
                  competitive advantage.
                </p>
              </motion.div>
            </div>
          </>;
      case 'workflow':
        return <>
            <div className="space-y-12 w-full" data-id="element-1941">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1942">
                {slide.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 border border-stone-800" data-id="element-1943">
                {slide.columns?.map((col, colIdx) => <div key={colIdx} className={`${colIdx === 0 ? 'border-r border-stone-800 bg-stone-900/20' : 'bg-stone-900/60'}`} data-id="element-1944">
                    <div className="p-6 border-b border-stone-800" data-id="element-1945">
                      <p className={`font-mono text-sm uppercase tracking-widest ${colIdx === 0 ? 'text-stone-500' : 'text-red-600'}`} data-id="element-1946">
                        {col.title}
                      </p>
                    </div>
                    <div className="p-6 space-y-8" data-id="element-1947">
                      {col.items.map((item, i) => <motion.div key={i} custom={i + colIdx * 5} variants={itemVariants} initial="hidden" animate="visible" className="flex items-start gap-4" data-id="element-1948">
                          <span className={`font-mono text-sm mt-1 ${colIdx === 0 ? 'text-stone-600' : 'text-red-600'}`} data-id="element-1949">
                            0{i + 1}
                          </span>
                          <p className={`text-xl ${colIdx === 0 ? 'text-stone-400' : 'text-stone-50'}`} data-id="element-1950">
                            {item}
                          </p>
                        </motion.div>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </>;
      case 'product':
        return <>
            <div className="space-y-12 w-full" data-id="element-1951">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1952">
                {slide.title}
              </h2>
              <div className="border-t border-stone-800" data-id="element-1953">
                {slide.bullets?.map((bullet, i) => {
                const [title, ...desc] = bullet.split(' (');
                const cleanDesc = desc.join(' (').replace(')', '');
                return <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="py-6 border-b border-stone-800 grid grid-cols-12 gap-4 items-center" data-id="element-1954">
                      <div className="col-span-2 md:col-span-1" data-id="element-1955">
                        <span className="font-mono text-sm text-stone-500" data-id="element-1956">
                          0{i + 1}
                        </span>
                      </div>
                      <div className="col-span-10 md:col-span-4" data-id="element-1957">
                        <p className="font-medium text-lg text-stone-50" data-id="element-1958">
                          {title}
                        </p>
                      </div>
                      <div className="col-span-12 md:col-span-7 mt-2 md:mt-0" data-id="element-1959">
                        <p className="text-stone-400 text-lg" data-id="element-1960">
                          {cleanDesc || title}
                        </p>
                      </div>
                    </motion.div>;
              })}
              </div>
            </div>
          </>;
      case 'resorts':
        return <>
            <div className="space-y-10 w-full" data-id="element-1961">
              <div data-id="element-1962">
                <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-1963">
                  Value Proposition
                </p>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50" data-id="element-1964">
                  {slide.title}
                </h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-16" data-id="element-1965">
                <div className="lg:col-span-5 space-y-12" data-id="element-1966">
                  {[{
                  val: '$6M+',
                  label: 'Estimated Annual Savings'
                }, {
                  val: '3x',
                  label: 'Faster Conversion'
                }, {
                  val: '60%+',
                  label: 'Workload Reduction'
                }].map((stat, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" data-id="element-1967">
                      <p className="font-mono text-6xl text-stone-50 mb-2" data-id="element-1968">
                        {stat.val}
                      </p>
                      <p className="font-mono text-sm uppercase tracking-widest text-red-600" data-id="element-1969">
                        {stat.label}
                      </p>
                    </motion.div>)}
                </div>
                <div className="lg:col-span-7 space-y-8" data-id="element-1970">
                  {slide.bullets?.map((bullet, i) => {
                  const [title, desc] = bullet.split(' (');
                  return <motion.div key={i} custom={i + 3} variants={itemVariants} initial="hidden" animate="visible" className="border-l-2 border-stone-800 pl-6 hover:border-red-600 transition-colors duration-300" data-id="element-1971">
                        <p className="text-2xl text-stone-50 font-medium mb-2" data-id="element-1972">
                          {title}
                        </p>
                        {desc && <p className="text-lg text-stone-400" data-id="element-1973">
                            {desc.replace(')', '')}
                          </p>}
                      </motion.div>;
                })}
                </div>
              </div>
            </div>
          </>;
      case 'agencies':
        const agencyIcons = [Zap, Clock, BarChart3, Trophy, FileText];
        return <>
            <div className="space-y-10 w-full" data-id="element-1974">
              <div data-id="element-1975">
                <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-1976">
                  Distribution Channel
                </p>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50" data-id="element-1977">
                  {slide.title}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1978">
                {slide.bullets?.map((bullet, i) => {
                const Icon = agencyIcons[i] || Zap;
                const [title, desc] = bullet.split(' (');
                return <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-8 border border-stone-800 bg-stone-900/40 hover:bg-stone-900 transition-colors" data-id="element-1979">
                      <Icon className="w-8 h-8 text-red-600 mb-6" data-id="element-1980" />
                      <h3 className="text-xl font-medium text-stone-50 mb-3" data-id="element-1981">
                        {title}
                      </h3>
                      {desc && <p className="text-stone-400" data-id="element-1982">
                          {desc.replace(')', '')}
                        </p>}
                    </motion.div>;
              })}
              </div>
            </div>
          </>;
      case 'business-model':
        return <>
            <div className="space-y-12 w-full" data-id="element-1983">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-1984">
                {slide.title}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center" data-id="element-1985">
                <div className="lg:col-span-5" data-id="element-1986">
                  <motion.div initial={{
                  scale: 0.9,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.5
                }} data-id="element-1987">
                    <p className="font-mono text-[12rem] leading-none text-stone-50 tracking-tighter" data-id="element-1988">
                      5<span className="text-red-600" data-id="element-1989">%</span>
                    </p>
                    <p className="font-mono text-xl uppercase tracking-widest text-stone-400 mt-4" data-id="element-1990">
                      of verified net savings
                    </p>
                  </motion.div>
                </div>
                <div className="lg:col-span-7" data-id="element-1991">
                  <div className="space-y-6" data-id="element-1992">
                    {slide.bullets?.slice(0, 4).map((bullet, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="flex items-start gap-4 p-6 border border-stone-800 bg-stone-900/30" data-id="element-1993">
                        <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0" data-id="element-1994" />
                        <p className="text-xl text-stone-300" data-id="element-1995">{bullet}</p>
                      </motion.div>)}
                    <motion.p custom={4} variants={itemVariants} initial="hidden" animate="visible" className="font-mono text-sm text-stone-500 pt-4" data-id="element-1996">
                      * {slide.bullets?.[4]}
                    </motion.p>
                  </div>
                </div>
              </div>
            </div>
          </>;
      case 'financials':
        return <>
            <div className="space-y-12 w-full" data-id="element-1997">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 mb-4" data-id="element-1998">
                {slide.title}
              </h2>
              <p className="font-mono text-sm text-stone-500 mb-8" data-id="element-1999">
                Savings = Group Room Revenue × Wholesale Leakage % × Adoption %
                &nbsp;|&nbsp; BQ Share = Savings × 5%
              </p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="border border-stone-800 rounded-lg overflow-hidden" data-id="element-2000">
                <table className="w-full text-left" data-id="element-2001">
                  <thead className="bg-stone-900 border-b border-stone-800" data-id="element-2002">
                    <tr data-id="element-2003">
                      {['Scenario', 'Group Rev', 'Leakage %', 'Adoption', 'Savings', 'BQ Share (5%)'].map((h, i) => <th key={i} className={`p-6 font-mono text-xs uppercase tracking-widest ${i >= 4 ? 'text-red-600' : 'text-stone-500'}`} data-id="element-2004">
                          {h}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-800 bg-stone-950" data-id="element-2005">
                    {slide.table?.map((row, i) => <motion.tr key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="hover:bg-stone-900/50 transition-colors" data-id="element-2006">
                        <td className="p-6 font-medium text-stone-50" data-id="element-2007">
                          {row.scenario}
                        </td>
                        <td className="p-6 text-stone-400 font-mono" data-id="element-2008">
                          {row.rev}
                        </td>
                        <td className="p-6 text-stone-400 font-mono" data-id="element-2009">
                          {row.wholesale}
                        </td>
                        <td className="p-6 text-stone-400 font-mono" data-id="element-2010">
                          {row.adoption}
                        </td>
                        <td className="p-6 text-stone-50 font-mono text-xl" data-id="element-2011">
                          {row.savings}
                        </td>
                        <td className="p-6 text-red-600 font-mono text-xl" data-id="element-2012">
                          {row.share}
                        </td>
                      </motion.tr>)}
                  </tbody>
                </table>
                <div className="p-4 bg-stone-900 border-t border-stone-800" data-id="element-2013">
                  <p className="font-mono text-xs text-stone-500" data-id="element-2014">
                    * Per resort group. Scales with portfolio size.
                  </p>
                </div>
              </motion.div>
            </div>
          </>;
      case 'ai':
        return <div className="space-y-10 w-full" data-id="element-2015">
            <div data-id="element-2016">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-2017">
                RateBrain Intelligence
              </p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 mb-4" data-id="element-2018">
                AI-Powered Pricing Intelligence
              </h2>
              <p className="text-xl text-stone-400 max-w-3xl" data-id="element-2019">
                RateBrain analyzes competitive pricing, demand patterns, and
                market intelligence to deliver optimal dynamic pricing for every
                group quote.
              </p>
              <div className="grid grid-cols-1 gap-4" data-id="element-2020">
                {slide.bullets?.map((bullet, i) => {
                const [title, desc] = bullet.split(': ');
                return <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-6 border border-stone-800 bg-stone-900/30 flex items-start gap-6 hover:border-stone-600 transition-colors" data-id="element-2021">
                      <span className="font-mono text-2xl text-red-600 mt-1" data-id="element-2022">
                        0{i + 1}
                      </span>
                      <div data-id="element-2023">
                        <h3 className="text-xl font-medium text-stone-50 mb-2" data-id="element-2024">
                          {title}
                        </h3>
                        <p className="text-stone-400 text-lg" data-id="element-2025">{desc}</p>
                      </div>
                    </motion.div>;
              })}
              </div>
            </div>
          </div>;
      case 'gtm':
        const gtmIcons = [Network, Target, Clock, TrendingUp, Users];
        return <>
            <div className="space-y-12 w-full" data-id="element-2026">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-2027">
                {slide.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2028">
                {slide.bullets?.map((bullet, i) => {
                const Icon = gtmIcons[i] || Target;
                return <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className={`p-8 border border-stone-800 bg-stone-900/40 ${i === 2 ? 'border-red-600/50 bg-red-950/10' : ''}`} data-id="element-2029">
                      <Icon className={`w-8 h-8 mb-6 ${i === 2 ? 'text-red-600' : 'text-stone-500'}`} data-id="element-2030" />
                      <p className={`text-xl leading-relaxed ${i === 2 ? 'text-stone-50' : 'text-stone-300'}`} data-id="element-2031">
                        {bullet}
                      </p>
                    </motion.div>;
              })}
              </div>
            </div>
          </>;
      case 'roadmap':
        return <>
            <div className="space-y-16 w-full" data-id="element-2032">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 mb-12" data-id="element-2033">
                {slide.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 border border-stone-800" data-id="element-2034">
                {slide.timeline?.map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className={`p-8 ${i !== 3 ? 'border-b md:border-b-0 md:border-r border-stone-800' : ''} 
                  ${i === 0 ? 'bg-stone-900' : ''} 
                  ${i === 3 ? 'border-l-2 border-l-red-600 md:border-l-0 md:border-t-2 md:border-t-red-600' : ''}`} data-id="element-2035">
                    <div className="flex items-center justify-between mb-8" data-id="element-2036">
                      <p className={`font-mono text-sm uppercase tracking-widest ${i === 3 ? 'text-red-600' : 'text-stone-500'}`} data-id="element-2037">
                        {item.phase}
                      </p>
                      <p className={`font-mono text-xs ${i === 0 ? 'text-red-600' : 'text-stone-600'}`} data-id="element-2038">
                        {item.date}
                      </p>
                    </div>
                    <p className="text-lg text-stone-300 leading-relaxed" data-id="element-2039">
                      {item.desc}
                    </p>
                  </motion.div>)}
              </div>
            </div>
          </>;
      case 'next-steps':
        const steps = slide.bullets?.slice(0, -1) || [];
        const timeline = slide.bullets?.[slide.bullets.length - 1] || '';
        return <div className="space-y-12 w-full" data-id="element-2040">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-2041">
              {slide.title}
            </h2>

            <div className="space-y-6 mb-16" data-id="element-2042">
              {steps.map((step, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="flex items-center gap-6 p-6 border border-stone-800 bg-stone-900/20" data-id="element-2043">
                  <div className="w-8 h-8 rounded-full border-2 border-stone-700 flex items-center justify-center flex-shrink-0" data-id="element-2044">
                    <div className="w-3 h-3 rounded-full bg-transparent" data-id="element-2045" />
                  </div>
                  <p className="text-2xl text-stone-300" data-id="element-2046">{step}</p>
                </motion.div>)}
            </div>
            <motion.div custom={steps.length} variants={itemVariants} initial="hidden" animate="visible" className="p-8 border border-red-900/30 bg-red-950/10" data-id="element-2047">
              <p className="font-mono text-sm uppercase tracking-widest text-red-600 mb-4" data-id="element-2048">
                Timeline
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xl text-stone-50" data-id="element-2049">
                {timeline.replace('Timeline: ', '').split(' → ').map((t, i, arr) => <Fragment key={i} data-id="element-2050">
                      <span data-id="element-2051">{t}</span>
                      {i < arr.length - 1 && <ArrowRight className="w-5 h-5 text-stone-600" data-id="element-2052" />}
                    </Fragment>)}
              </div>
            </motion.div>
          </div>;
      default:
        return null;
    }
  };
  return <>
      <div className="h-screen w-full bg-stone-950 text-stone-50 overflow-hidden flex flex-col font-sans selection:bg-red-600/30" data-id="element-2053">
        {/* Top Bar */}
        <div className="flex items-center justify-between p-6 z-10 relative border-b border-stone-900/50" data-id="element-2054">
          <Link to="/" className="flex items-center text-stone-400 hover:text-stone-50 transition-colors font-mono text-sm uppercase tracking-widest" data-id="element-2055">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2056" />
            Back to site
          </Link>
          <div className="text-stone-500 font-mono text-sm" data-id="element-2057">
            {String(currentIndex + 1).padStart(2, '0')}{' '}
            <span className="text-stone-700" data-id="element-2058">/</span>{' '}
            {String(SLIDES.length).padStart(2, '0')}
          </div>
          <button onClick={() => setShowNotes(!showNotes)} className={`flex items-center px-4 py-2 rounded-none font-mono text-sm uppercase tracking-widest transition-colors border ${showNotes ? 'bg-red-600/10 text-red-600 border-red-600/30' : 'text-stone-400 border-transparent hover:text-stone-50 hover:bg-stone-900'}`} data-id="element-2059">
            <MessageSquareText className="w-4 h-4 mr-2" data-id="element-2060" />
            Notes
          </button>
        </div>

        {/* Main Slide Area */}
        <div className="flex-1 relative flex items-start justify-center px-12 md:px-24 overflow-y-auto" data-id="element-2061">
          <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-2062">
            <motion.div key={currentIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} className="w-full max-w-6xl mx-auto pt-8 pb-12" data-id="element-2063">
              {renderSlideContent(slide)}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Arrows */}
          <button onClick={() => paginate(-1)} disabled={currentIndex === 0} className="absolute left-6 p-4 text-stone-600 hover:text-stone-50 hover:bg-stone-900 disabled:opacity-0 transition-all border border-transparent hover:border-stone-800" data-id="element-2064">
            <ChevronLeft className="w-8 h-8" data-id="element-2065" />
          </button>
          <button onClick={() => paginate(1)} disabled={currentIndex === SLIDES.length - 1} className="absolute right-6 p-4 text-stone-600 hover:text-stone-50 hover:bg-stone-900 disabled:opacity-0 transition-all border border-transparent hover:border-stone-800" data-id="element-2066">
            <ChevronRight className="w-8 h-8" data-id="element-2067" />
          </button>
        </div>

        {/* Speaker Notes Panel */}
        <AnimatePresence data-id="element-2068">
          {showNotes && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="bg-stone-900 border-t border-stone-800 p-8" data-id="element-2069">
              <div className="max-w-6xl mx-auto" data-id="element-2070">
                <h4 className="text-red-600 font-mono text-xs uppercase tracking-widest mb-4" data-id="element-2071">
                  Speaker Notes
                </h4>
                <p className="text-stone-300 text-lg leading-relaxed font-sans" data-id="element-2072">
                  {slide.note}
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Progress Dots */}
        <div className="flex justify-center gap-3 p-6 z-10 relative border-t border-stone-900/50" data-id="element-2073">
          {SLIDES.map((_, i) => <button key={i} onClick={() => {
          setDirection(i > currentIndex ? 1 : -1);
          setCurrentIndex(i);
        }} className={`h-1 transition-all duration-300 ${i === currentIndex ? 'bg-red-600 w-8' : 'bg-stone-800 hover:bg-stone-600 w-4'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-2074" />)}
        </div>
      </div>
    </>;
}